/**
 * Argus, as a lens.
 *
 * This is the entire seam between Argus and the application it hangs on. Argus
 * registers a lens here and reads the shell's state; the shell holds a name and
 * calls `isAvailable()`. Nothing in `app-root`, `store.ts` or `lens.ts` knows
 * what Argus is, and deleting this directory leaves an unclaimed name that
 * resolves to the plain application - which is rule 1 and rule 2 of `docs/plans/argus.md`
 * made mechanical rather than promised.
 *
 * The rooms live here too, for the same reason: the shell renders whatever the
 * active section's component gives it, and the list of Argus rooms is not the
 * shell's business.
 */

import { hasFlag } from '../../state/feature-flags.ts';
import { lensIsOn, registerLens, setLens, toggleLens } from '../../state/lens.ts';
import { Atom } from '../../state/atom.ts';
import { registerSectionRoute } from '../../state/app-route.ts';

/** The stored name. Short, lowercase: it travels in a link and in a row. */
export const ARGUS_LENS = 'argus';

/**
 * Whether this account has Argus at all: the Features decision, which already
 * folds in By role (an Argus role) or an explicit audience. No administrative
 * bypass - the server refuses the same way. See `docs/plans/access-management.md`.
 */
export function hasArgus(): boolean {
  return hasFlag('argus');
}

/**
 * The lens switch is out of the navigation for now (2026-10-22). While it is
 * out, the lens is unavailable to everyone, so nobody is left with it on and no
 * way to turn it off. A stored choice is kept, not cleared: when the switch
 * comes back, so does each person's lens.
 */
let lensPaused = true;

/** For tests, which exercise the lens as if the switch were back. */
export function setArgusLensPaused(paused: boolean): void {
  lensPaused = paused;
}

registerLens({
  id: ARGUS_LENS,
  label: 'ARGUS',
  description: 'Show what is expected, and where it broke.',
  isAvailable: () => !lensPaused && hasArgus(),
});

/** Whether the Argus lens is in force right now. */
export function argusOn(): boolean {
  return lensIsOn(ARGUS_LENS);
}

/** Put the lens on or take it off. The control in the header calls this. */
export function toggleArgus(): void {
  toggleLens(ARGUS_LENS);
  onLensChanged();
}

/**
 * What has to happen when the lens is flipped.
 *
 * The marks are fetched here rather than by the provider, which is called
 * during a draw and must answer from what it already has. The chart is then
 * redrawn so the marks appear without anybody pressing Plot again - the lens
 * changes what is drawn, and the data is not re-queried (`docs/plans/argus.md` rule 7).
 *
 * Imported lazily: `argus-overlays` imports this module, and the redraw reaches
 * into the plot. Neither belongs in the import graph of a file the header loads
 * before anybody has asked for Argus at all.
 */
function onLensChanged(): void {
  if (!argusOn()) {
    void import('../plot/plot-actions.ts').then((m) => m.rerenderChartFromLast());
    return;
  }
  void import('./argus-overlays.ts').then(async (overlays) => {
    await overlays.loadArgusMarks();
    const plot = await import('../plot/plot-actions.ts');
    plot.rerenderChartFromLast();
  });
}

/** Put the lens on, without taking it off if it already is. */
export function turnArgusOn(): void {
  setLens(ARGUS_LENS);
}

/**
 * The rooms, in the order the tab row draws them.
 *
 * Every one of them is empty in A0 and says so in its own words. An empty state
 * that names the phase it is waiting for is the honest version of a screen that
 * does nothing yet; a fake list would be the dishonest one. See `docs/plans/argus.md`.
 */
export type ArgusRoom = 'inbox' | 'rules' | 'backtest' | 'history' | 'health' | 'discoveries';

export interface ArgusRoomInfo {
  id: ArgusRoom;
  label: string;
  /** What will live here, in the words a person would use. */
  blurb: string;
  /** What has to be built before it does. */
  waitingFor: string;
}

export const ARGUS_ROOMS: readonly ArgusRoomInfo[] = [
  {
    id: 'inbox',
    label: 'Inbox',
    blurb:
      'What the watcher found still going on, newest first, and what it found before. A ' +
      'suspicion to look at, never a verdict.',
    waitingFor: 'Built: open alerts, one per rule with its ships inside, each reviewed on its own page and closed with a verdict.',
  },
  {
    id: 'rules',
    label: 'Rules',
    blurb:
      'What is worth watching, written as a sentence you can read aloud: which entity, which ' +
      'signal, when it counts, compared to what, and how far off is too far.',
    waitingFor: 'A1 - rule storage, the eight slots, and the group leader’s deploy.',
  },
  {
    id: 'backtest',
    label: 'Backtest',
    blurb:
      'Ask when something happened: a question in blocks, run by the watcher over a window you ' +
      'choose, answered with episodes per entity. Every rule backtest is here too.',
    waitingFor: 'Built: questions, rule backtests, the window, and where they fired.',
  },
  {
    id: 'history',
    label: 'History',
    blurb: 'What the watcher found that is over: ended, dismissed, or no longer watched.',
    waitingFor: 'Built: past alerts with their verdicts. Whether things got better arrives later.',
  },
  {
    id: 'health',
    label: 'Health',
    blurb:
      'The Argus cluster: the scheduler, the worker pools and their named workers, the queues, ' +
      'late data and failed jobs, and what they did over the last hours. Admins only.',
    waitingFor: 'Built: the cluster now and its statistics. The live stage and Replay come next.',
  },
  {
    id: 'discoveries',
    label: 'Discoveries',
    blurb:
      'Things a model noticed that nobody wrote a rule for. Kept apart from the Inbox until ' +
      'they earn their place; promote one and it becomes an ordinary rule.',
    waitingFor: 'A7, and only if machine learning ever arrives. Argus does not wait for it.',
  },
];

/** The room on screen, and the second half of the path: `/argus/rules`. */
export const argusRoom = new Atom<ArgusRoom>('inbox');

/**
 * Counts clicks on the room you are already in. A room listens and goes back
 * to its top level - the Rules room leaves the editor for the list - so the
 * tab is also the way home, as a section's own button is.
 */
export const argusRoomReselected = new Atom<number>(0);

/**
 * The header's line about where the data stands, as the watchers last said.
 * Set by the Rules store from `GET /api/argus/watchers`; until anything has
 * been asked it says nothing is connected, which is the truth until proven
 * otherwise (rule 9).
 */
export const argusClock = new Atom<string>('Daily clock · no watcher connected');

/**
 * The count beside ARGUS: open episodes nobody has marked, and the worst
 * severity among them. Zero draws nothing (rule 10). Set by
 * `argus-inbox-store.ts`, which asks only for people who have Argus (rule 6).
 */
export const argusAttention = new Atom<{ unseen: number; worst: string | null; followed?: number }>({
  unseen: 0,
  worst: null,
  followed: 0,
});

/**
 * The episode on the triage page, or null for the room's own list. Lives in
 * the address as the room's second half - `/argus/inbox/42` - so an alert, a
 * digest or a colleague can link straight to it.
 */
export const argusEpisode = new Atom<number | null>(null);

function syncUrl(): void {
  // Loaded when a room changes, not with the lens: url-sync pulls in most of
  // the application's state, which the lens has no reason to carry.
  void import('../../state/url-sync.ts').then((m) => m.syncToUrl());
}

/** Open one episode's triage page, from the Inbox, History or a timeline. */
export function openArgusEpisode(id: number | null): void {
  if (id !== null && argusRoom.value !== 'inbox' && argusRoom.value !== 'history') {
    argusRoom.set('inbox');
  }
  argusEpisode.set(id);
  syncUrl();
}

export function setArgusRoom(room: ArgusRoom): void {
  if (room === argusRoom.value) {
    // The tab is also the way home: from an episode back to its room's list.
    if (argusEpisode.value !== null) {
      openArgusEpisode(null);
      return;
    }
    argusRoomReselected.set(argusRoomReselected.value + 1);
    return;
  }
  argusEpisode.set(null);
  argusRoom.set(room);
  syncUrl();
}

// The section's own address, registered rather than written into Explorer's
// routing: `/argus/<room>`, so a refresh or a shared link comes back here.
registerSectionRoute({
  section: 'argus',
  sub: () =>
    argusEpisode.value !== null && (argusRoom.value === 'inbox' || argusRoom.value === 'history')
      ? `${argusRoom.value}/${argusEpisode.value}`
      : argusRoom.value,
  restoreSub: (sub) => {
    const [given, second] = sub.split('/');
    // The room was called Quicktest in 1.14.0; its links still land.
    const first = given === 'quicktest' ? 'backtest' : given;
    const room = ARGUS_ROOMS.find((r) => r.id === first);
    if (!room) return;
    argusRoom.set(room.id);
    const id = second && /^\d+$/.test(second) ? Number(second) : null;
    argusEpisode.set(id !== null && (room.id === 'inbox' || room.id === 'history') ? id : null);
  },
});
